import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';

const ORIGIN = 'https://brainday.app';

/** Public marketing + legal routes that should rank in search. Everything else
 * (app shell, auth, onboarding, coming-soon) is noindexed. Keep in sync with
 * public/sitemap.xml. */
const INDEXABLE = new Set(['/', '/how-it-works', '/habits', '/pricing', '/privacy', '/terms']);

/** Per-route <title>. The static index.html title covers the homepage and any
 * no-JS crawler; this keeps the browser tab accurate as an SPA user navigates.
 * Mounted once at the router root so it covers every route. */
const TITLES: Record<string, string> = {
  '/': 'Brainday — a calmer way to plan your day',
  '/pricing': 'Pricing — Brainday',
  '/how-it-works': 'How it works — Brainday',
  '/habits': 'No-shame habits — Brainday',
  '/privacy': 'Privacy — Brainday',
  '/terms': 'Terms — Brainday',
  '/login': 'Sign in — Brainday',
  '/coming-soon': 'Brainday — coming soon',
  '/app': 'Today — Brainday',
  '/app/inbox': 'Inbox — Brainday',
  '/app/routines': 'Routines — Brainday',
  '/app/routines/new': 'New routine — Brainday',
  '/app/habits': 'Habits — Brainday',
  '/app/calendar': 'Calendar — Brainday',
  '/app/settings': 'Settings — Brainday',
};

function titleFor(pathname: string): string {
  const exact = TITLES[pathname];
  if (exact) return exact;
  if (pathname.startsWith('/app/routines')) return 'Routines — Brainday';
  if (pathname.startsWith('/onboarding')) return 'Welcome — Brainday';
  return 'Brainday';
}

/** Keep the head's indexing signals honest per route. The static index.html
 * ships a homepage canonical on EVERY route (single-page app), which made
 * Google file /pricing etc. as "Alternate page with proper canonical tag" and
 * refuse to index them. Googlebot indexes the rendered DOM, so correcting the
 * tags here is what it actually reads. */
function syncIndexingMeta(pathname: string) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
  const url = path === '/' ? `${ORIGIN}/` : `${ORIGIN}${path}`;
  const indexable = INDEXABLE.has(path);

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (indexable) {
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;
  } else {
    // A canonical on a noindex page sends Google conflicting signals — drop it.
    canonical?.remove();
  }

  document.head
    .querySelector('meta[name="robots"]')
    ?.setAttribute('content', indexable ? 'index, follow' : 'noindex');
  document.head.querySelector('meta[property="og:url"]')?.setAttribute('content', url);
}

export function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleFor(pathname);
    syncIndexingMeta(pathname);
  }, [pathname]);
  return <Outlet />;
}

export default RouteMeta;
